import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Users, UserPlus, Edit, Trash2, Search, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function UserManagement({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Use preview role if admin is previewing, otherwise use actual role
  const effectiveRole = previewRole || user.role;
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    role: "",
    dealership: "",
    department: "",
    password: ""
  });
  const [createFormData, setCreateFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "department_admin",
    dealership: "",
    department: ""
  });

  // Handle "View As" user - switches to that user's perspective
  const handleViewAsUser = (targetUser) => {
    if (targetUser.id === user.id) {
      // Clear preview if selecting self
      setPreviewRole(null);
      setPreviewDealership(null);
      setPreviewDepartment(null);
      toast.success("Returned to your own view");
    } else {
      setPreviewRole(targetUser.role);
      setPreviewDealership(targetUser.dealership || null);
      setPreviewDepartment(targetUser.department || null);
      toast.success(`Now viewing as ${targetUser.name}`);
    }
    navigate("/dashboard");
  };

  useEffect(() => {
    fetchUsers();
    fetchDealerships();
    fetchDepartments();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, sortField, sortDirection]);

  // Set default dealership for Site Admins
  useEffect(() => {
    if (effectiveRole === "site_admin" && user.dealership) {
      setCreateFormData(prev => ({
        ...prev,
        dealership: user.dealership
      }));
    }
  }, [effectiveRole, user.dealership]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchDealerships = async () => {
    try {
      const response = await axios.get(`${API}/config/dealerships`);
      setDealerships(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/config/departments`);
      setDepartments(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const filterUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (u) =>
          u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (u.dealership && u.dealership.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.department && u.department.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortField] || "";
      let bValue = b[sortField] || "";

      // Convert to lowercase for string comparison
      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredUsers(sorted);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      dealership: user.dealership || "none", // Convert empty/null to "none" for Select component
      department: user.department || "",
      password: ""
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Convert "none" back to empty string for backend
      const userData = { 
        ...editFormData, 
        dealership: editFormData.dealership === "none" ? "" : editFormData.dealership 
      };
      if (!userData.password) {
        delete userData.password;
      }
      
      await axios.patch(`${API}/admin/users/${selectedUser.id}`, userData);
      toast.success("User updated successfully");
      setShowEditModal(false);
      setSelectedUser(null);
      setEditFormData({
        name: "",
        email: "",
        role: "",
        dealership: "",
        department: "",
        password: ""
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Convert "none" back to empty string for backend
      const userData = { 
        ...createFormData, 
        dealership: createFormData.dealership === "none" ? "" : createFormData.dealership 
      };
      await axios.post(`${API}/admin/users/create`, userData);
      toast.success("User created successfully");
      setShowCreateModal(false);
      setCreateFormData({
        name: "",
        email: "",
        password: "",
        role: "department_admin",
        dealership: "",
        department: ""
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`);
      toast.success("User deleted successfully");
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      super_admin: "bg-pink-100 text-pink-700",
      admin: "bg-purple-100 text-purple-700",
      site_admin: "bg-orange-100 text-orange-700",
      department_admin: "bg-blue-100 text-blue-700",
      end_user: "bg-gray-100 text-gray-70 dark:text-white0"
    };
    return classes[role] || classes.end_user;
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: "Super Admin",
      admin: "Admin",
      site_admin: "Site Admin",
      department_admin: "Department Admin",
      end_user: "End User"
    };
    return labels[role] || role;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <span className="text-slate-400 dark:text-white ml-1">↕</span>;
    }
    return sortDirection === "asc" ? (
      <span className="text-blue-600 ml-1">↑</span>
    ) : (
      <span className="text-blue-600 ml-1">↓</span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-60 dark:text-white0">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-80 dark:text-white0" data-testid="user-management-title">
              User Management
            </h1>
            <p className="text-slate-600 dark:text-white mt-1">Manage user accounts and permissions</p>
          </div>
          {(effectiveRole === "admin" || effectiveRole === "super_admin") && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-user-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0">Total Users</p>
                  <p className="text-3xl font-bold text-slate-800 dark:text-white mt-2">{users.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0">Super Admins</p>
                  <p className="text-3xl font-bold text-pink-600 mt-2">
                    {users.filter(u => u.role === "super_admin").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0">Admins</p>
                  <p className="text-3xl font-bold text-purple-600 mt-2">
                    {users.filter(u => u.role === "admin").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0">Site Admins</p>
                  <p className="text-3xl font-bold text-orange-600 mt-2">
                    {users.filter(u => u.role === "site_admin").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0">Dept Admins</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">
                    {users.filter(u => u.role === "department_admin").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0">End Users</p>
                  <p className="text-3xl font-bold text-gray-600 dark:text-white mt-2">
                    {users.filter(u => u.role === "end_user").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6 border-0 shadow-md">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-white w-4 h-4" />
              <Input
                type="text"
                placeholder="Search users by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b border-slate-100">
            <CardTitle>All Users ({filteredUsers.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("name")}
                      data-testid="sort-by-name"
                    >
                      <div className="flex items-center">
                        User
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("role")}
                      data-testid="sort-by-role"
                    >
                      <div className="flex items-center">
                        Role
                        <SortIcon field="role" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("dealership")}
                      data-testid="sort-by-dealership"
                    >
                      <div className="flex items-center">
                        Dealership
                        <SortIcon field="dealership" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("department")}
                      data-testid="sort-by-department"
                    >
                      <div className="flex items-center">
                        Department
                        <SortIcon field="department" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("created_at")}
                      data-testid="sort-by-created"
                    >
                      <div className="flex items-center">
                        Created
                        <SortIcon field="created_at" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-80 dark:text-white0">{u.name}</div>
                        <div className="text-sm text-slate-50 dark:text-white0">{u.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className={getRoleBadgeClass(u.role)}>
                          {getRoleLabel(u.role)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-60 dark:text-white0">
                        {u.dealership || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-60 dark:text-white0">
                        {u.department || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-50 dark:text-white0">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(effectiveRole === "admin" || effectiveRole === "super_admin") ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAsUser(u)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title={`View as ${u.name}`}
                              data-testid={`view-as-user-${u.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(u)}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(u);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-400 dark:text-white text-xs">View Only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user information and permissions</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editFormData.role}
                  onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="end_user">End User</SelectItem>
                    {(effectiveRole === "admin" || effectiveRole === "super_admin") && (
                      <>
                        <SelectItem value="department_admin">Department Admin</SelectItem>
                        <SelectItem value="site_admin">Site Admin</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </>
                    )}
                    {effectiveRole === "super_admin" && (
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-dealership">Dealership</Label>
                  <Select 
                    value={editFormData.dealership} 
                    onValueChange={(value) => setEditFormData({ ...editFormData, dealership: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select dealership" />
                    </SelectTrigger>
                    <SelectContent>
                      {(effectiveRole === "admin" || effectiveRole === "super_admin") && (
                        <SelectItem value="none">No Dealership</SelectItem>
                      )}
                      {(effectiveRole === "admin" || effectiveRole === "super_admin") ? (
                        dealerships.map(dealership => (
                          <SelectItem key={dealership} value={dealership}>
                            {dealership}
                          </SelectItem>
                        ))
                      ) : (
                        // Site Admins can only assign to their dealership
                        user.dealership && (
                          <SelectItem key={user.dealership} value={user.dealership}>
                            {user.dealership}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-department">Department</Label>
                  <Select
                    value={editFormData.department}
                    onValueChange={(value) => setEditFormData({ ...editFormData, department: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.length > 0 ? (
                        departments.map((dept) => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Administration">Administration</SelectItem>
                          <SelectItem value="IT">IT</SelectItem>
                          <SelectItem value="Sales">Sales</SelectItem>
                          <SelectItem value="Service">Service</SelectItem>
                          <SelectItem value="Parts">Parts</SelectItem>
                          <SelectItem value="Finance">Finance</SelectItem>
                          <SelectItem value="Management">Management</SelectItem>
                          <SelectItem value="HR">HR</SelectItem>
                          <SelectItem value="Marketing">Marketing</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">New Password (optional)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  placeholder="Leave blank to keep current password"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>Create a new IT staff or admin account</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Name</Label>
                <Input
                  id="create-name"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                  required
                  data-testid="create-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  required
                  data-testid="create-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-password">Password</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                  required
                  data-testid="create-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-role">Role</Label>
                <Select
                  value={createFormData.role}
                  onValueChange={(value) => setCreateFormData({ ...createFormData, role: value })}
                >
                  <SelectTrigger data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="end_user">End User</SelectItem>
                    {(effectiveRole === "admin" || effectiveRole === "super_admin") && (
                      <>
                        <SelectItem value="department_admin">Department Admin</SelectItem>
                        <SelectItem value="site_admin">Site Admin</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </>
                    )}
                    {effectiveRole === "super_admin" && (
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-dealership">Dealership</Label>
                  <Select 
                    value={createFormData.dealership} 
                    onValueChange={(value) => setCreateFormData({ ...createFormData, dealership: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select dealership" />
                    </SelectTrigger>
                    <SelectContent>
                      {(effectiveRole === "admin" || effectiveRole === "super_admin") && (
                        <SelectItem value="none">No Dealership</SelectItem>
                      )}
                      {(effectiveRole === "admin" || effectiveRole === "super_admin") ? (
                        dealerships.map(dealership => (
                          <SelectItem key={dealership} value={dealership}>
                            {dealership}
                          </SelectItem>
                        ))
                      ) : (
                        // Site Admins can only assign to their dealership
                        user.dealership && (
                          <SelectItem key={user.dealership} value={user.dealership}>
                            {user.dealership}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-department">Department</Label>
                  <Select
                    value={createFormData.department}
                    onValueChange={(value) => setCreateFormData({ ...createFormData, department: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.length > 0 ? (
                        departments.map((dept) => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Administration">Administration</SelectItem>
                          <SelectItem value="IT">IT</SelectItem>
                          <SelectItem value="Sales">Sales</SelectItem>
                          <SelectItem value="Service">Service</SelectItem>
                          <SelectItem value="Parts">Parts</SelectItem>
                          <SelectItem value="Finance">Finance</SelectItem>
                          <SelectItem value="Management">Management</SelectItem>
                          <SelectItem value="HR">HR</SelectItem>
                          <SelectItem value="Marketing">Marketing</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-create-user">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user account for{" "}
              <strong>{selectedUser?.name}</strong> ({selectedUser?.email}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
